import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

const BoatExpensesEntry = () => {
  const { selectedFirmId } = useContext(AuthContext);

  // Master Data
  const [products, setProducts] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [trips, setTrips] = useState([]);

  // Form State
  const [tripId, setTripId] = useState(null);
  const [boatNo, setBoatNo] = useState('1');
  const [boatDate, setBoatDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [kataSale, setKataSale] = useState(0);
  const [jettiSale, setJettiSale] = useState(0);
  const [addEmployee, setAddEmployee] = useState(0);

  // Dynamic Expenses Table [{ product_id, qty, rate, Amount }]
  const [expenses, setExpenses] = useState([
    { product_id: '', qty: '', rate: '', Amount: '' }
  ]);

  // Selected Crew Members [{ emp_id, boat_commission }]
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadMasters();
    loadTrips();
  }, [selectedFirmId]);

  const loadMasters = async () => {
    try {
      const [prodRes, empRes] = await Promise.all([
        api.get(`/masters/products?firmId=${selectedFirmId}`),
        api.get(`/masters/employees?firmId=${selectedFirmId}`)
      ]);
      setProducts(prodRes.data);
      setAllEmployees(empRes.data);

      // Select ALL crew members by default for new trip entry
      if (!tripId) {
        setSelectedEmployees(empRes.data.map(emp => ({
          emp_id: emp.id,
          boat_commission: emp.commission || 1,
          Emp_name: emp.Emp_name
        })));
      }
    } catch (err) {
      console.error('Error loading masters', err);
    }
  };

  const loadTrips = async () => {
    try {
      const res = await api.get(`/boats/trips?firmId=${selectedFirmId}`);
      setTrips(res.data);
      if (res.data.length > 0 && !tripId) {
        // Auto increment next trip number
        const maxTripNo = Math.max(...res.data.map(t => Number(t.boat_no) || 0));
        setBoatNo(String(maxTripNo + 1));
      }
    } catch (err) {
      console.error('Error loading trips', err);
    }
  };

  // --- CALCULATION LOGIC ---
  const totalBoatExpenses = expenses.reduce((sum, item) => sum + (parseFloat(item.Amount) || 0), 0);
  const totalSales = (parseFloat(kataSale) || 0) + (parseFloat(jettiSale) || 0);
  const fiftyPercentShare = (totalSales - totalBoatExpenses) / 2.0;

  const baseCrewShares = selectedEmployees.reduce((sum, emp) => sum + (parseFloat(emp.boat_commission) || 1), 0);
  const totalCrewShares = baseCrewShares + (parseFloat(addEmployee) || 0);
  const perEmployeePayout = totalCrewShares > 0 ? (fiftyPercentShare / totalCrewShares) : 0;

  // --- EXPENSE GRID HANDLERS ---
  const handleExpenseChange = (index, field, value) => {
    const newExpenses = [...expenses];
    newExpenses[index][field] = value;

    // Auto calculate amount when qty or rate changes
    if (field === 'product_id') {
      const prod = products.find(p => String(p.Id) === String(value));
      if (prod) {
        newExpenses[index].rate = prod.PRate || 0;
      }
    }

    const qty = parseFloat(newExpenses[index].qty) || 0;
    const rate = parseFloat(newExpenses[index].rate) || 0;
    if (field === 'qty' || field === 'rate' || field === 'product_id') {
      newExpenses[index].Amount = qty * rate;
    }

    setExpenses(newExpenses);
  };

  const addExpenseRow = () => {
    setExpenses([...expenses, { product_id: '', qty: '', rate: '', Amount: '' }]);
  };

  const removeExpenseRow = (index) => {
    if (expenses.length === 1) return;
    setExpenses(expenses.filter((_, i) => i !== index));
  };

  // --- CREW SELECTION HANDLERS ---
  const toggleEmployee = (emp) => {
    const exists = selectedEmployees.find(e => e.emp_id === emp.id);
    if (exists) {
      // User deselects employee who is not available
      setSelectedEmployees(selectedEmployees.filter(e => e.emp_id !== emp.id));
    } else {
      // Select employee
      setSelectedEmployees([
        ...selectedEmployees,
        { emp_id: emp.id, boat_commission: emp.commission || 1, Emp_name: emp.Emp_name }
      ]);
    }
  };

  const selectAllCrew = () => {
    setSelectedEmployees(allEmployees.map(emp => ({
      emp_id: emp.id,
      boat_commission: emp.commission || 1,
      Emp_name: emp.Emp_name
    })));
  };

  const deselectAllCrew = () => {
    setSelectedEmployees([]);
  };

  const handleCrewShareChange = (empId, shareValue) => {
    setSelectedEmployees(selectedEmployees.map(e => {
      if (e.emp_id === empId) {
        return { ...e, boat_commission: parseFloat(shareValue) || 1 };
      }
      return e;
    }));
  };

  const handleSaveTrip = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validExpenses = expenses.filter(e => e.product_id && e.Amount > 0);
    if (selectedEmployees.length === 0) {
      setError('Please select at least one crew member for this boat trip.');
      return;
    }

    const payload = {
      id: tripId,
      boat_no: parseInt(boatNo) || 1,
      boat_date: boatDate,
      kata_sale: parseFloat(kataSale) || 0,
      jetti_sale: parseFloat(jettiSale) || 0,
      add_employee: parseFloat(addEmployee) || 0,
      firmid: selectedFirmId,
      fid: 4,
      expenses: validExpenses,
      employees: selectedEmployees
    };

    setLoading(true);
    try {
      await api.post('/boats/trips', payload);
      setSuccess('Boat trip entry saved & settled successfully!');
      resetForm();
      loadTrips();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save boat trip entry');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTrip = async (trip) => {
    try {
      setLoading(true);
      const res = await api.get(`/boats/trips/${trip.id}`);
      const { details, expenses: expList, employees: empList } = res.data;

      setTripId(details.id);
      setBoatNo(String(details.boat_no));
      setBoatDate(details.boat_date);
      setKataSale(details.kata_sale || 0);
      setJettiSale(details.jetti_sale || 0);
      setAddEmployee(details.add_employee || 0);

      setExpenses(expList.length > 0 ? expList : [{ product_id: '', qty: '', rate: '', Amount: '' }]);
      setSelectedEmployees(empList.map(e => ({ emp_id: e.emp_id, boat_commission: e.boat_commission, Emp_name: e.Emp_name })));
    } catch (err) {
      setError('Failed to load trip details for edit');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrip = async (id) => {
    if (!window.confirm('Delete boat trip entry? This will reverse employee payouts.')) return;
    try {
      await api.delete(`/boats/trips/${id}`);
      setSuccess('Boat trip deleted');
      loadTrips();
    } catch (err) {
      setError('Failed to delete trip');
    }
  };

  const resetForm = () => {
    setTripId(null);
    setKataSale(0);
    setJettiSale(0);
    setAddEmployee(0);
    setExpenses([{ product_id: '', qty: '', rate: '', Amount: '' }]);
    // Reset selected employees to ALL BY DEFAULT
    setSelectedEmployees(allEmployees.map(emp => ({
      emp_id: emp.id,
      boat_commission: emp.commission || 1,
      Emp_name: emp.Emp_name
    })));
  };

  return (
    <div className="app-container container-fluid py-3 px-2 px-md-4">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
        <div>
          <h3 className="fw-extrabold mb-1 text-primary fs-4" style={{ fontFamily: 'var(--app-font-heading)' }}>
            <i className="bi bi-water me-2"></i>पलट / Boat Trip & Expenses Settlement
          </h3>
          <p className="text-muted mb-0 small">Record boat expenses, Kata/Jetti sales, and compute 50-50 crew commission splits</p>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {success && <div className="alert alert-success py-2 small">{success}</div>}

      <div className="row g-4">
        {/* LEFT FORM: TRIP PARAMETERS & EXPENSES */}
        <div className="col-12 col-xl-8">
          <form onSubmit={handleSaveTrip}>
            {/* TRIP HEADER CARD */}
            <div className="card border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
              <div className="card-header bg-primary text-white fw-bold d-flex justify-content-between align-items-center py-3 px-4">
                <span className="d-flex align-items-center gap-2 fs-6">
                  <i className="bi bi-compass-fill fs-5"></i>
                  पलट तपशील / Trip Header & Sales
                </span>
                {tripId && <span className="badge bg-warning text-dark px-3 py-1 fs-6">Editing Trip #{boatNo}</span>}
              </div>
              <div className="card-body p-4 bg-white">
                <div className="row g-3">
                  <div className="col-12 col-sm-6 col-md-2">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-hash text-primary me-1"></i>
                      खेप क्र. (Trip No) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-lg fw-bold border-primary-subtle shadow-sm"
                      value={boatNo}
                      onChange={(e) => setBoatNo(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-12 col-sm-6 col-md-3">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-calendar3 text-primary me-1"></i>
                      दिनांक (Date) <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-lg border-primary-subtle shadow-sm"
                      value={boatDate}
                      onChange={(e) => setBoatDate(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-6 col-md-2.5">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-currency-rupee text-success me-1"></i>
                      काटा विक्री (Kata Sale)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-lg text-success fw-bold border-success-subtle shadow-sm"
                      value={kataSale}
                      onChange={(e) => setKataSale(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-2.5">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-currency-rupee text-success me-1"></i>
                      जेट्टी विक्री (Jetti Sale)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-lg text-success fw-bold border-success-subtle shadow-sm"
                      value={jettiSale}
                      onChange={(e) => setJettiSale(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <label className="form-label fw-bold small text-dark d-flex align-items-center">
                      <i className="bi bi-plus-circle-fill text-warning me-1"></i>
                      इतर वाटा (Extra Vata / Point)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      className="form-control form-control-lg text-primary fw-bold border-warning-subtle shadow-sm"
                      placeholder="0"
                      value={addEmployee}
                      onChange={(e) => setAddEmployee(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* EXPENSES GRID CARD */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0 text-dark">
                  <i className="bi bi-cart-dash me-2 text-danger"></i>बोट खर्च (Expenses: डीझेल, बर्फ, किराना, इत्यादी)
                </h6>
                <button type="button" className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={addExpenseRow}>
                  <i className="bi bi-plus-lg me-1"></i> नवीन खर्च (+ Add Line)
                </button>
              </div>

              {/* MOBILE EXPENSE CARDS VIEW (< 768px) */}
              <div className="d-block d-md-none p-2 bg-light">
                {expenses.map((row, idx) => (
                  <div key={idx} className="card border mb-2 shadow-sm rounded-3">
                    <div className="card-body p-2">
                      <div className="mb-2">
                        <label className="form-label small text-muted mb-1">खर्च प्रकार (Item)</label>
                        <select
                          className="form-select form-select-sm"
                          value={row.product_id}
                          onChange={(e) => handleExpenseChange(idx, 'product_id', e.target.value)}
                        >
                          <option value="">-- खर्च निवडा --</option>
                          {products.map(p => (
                            <option key={p.Id} value={p.Id}>{p.productName} ({p.unit})</option>
                          ))}
                        </select>
                      </div>
                      <div className="row g-2">
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">नग Qty</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-center"
                            value={row.qty}
                            onChange={(e) => handleExpenseChange(idx, 'qty', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">दर Rate</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-center"
                            value={row.rate}
                            onChange={(e) => handleExpenseChange(idx, 'rate', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <label className="form-label small text-muted mb-1">एकूण Amount</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-center fw-bold text-danger"
                            value={row.Amount}
                            onChange={(e) => handleExpenseChange(idx, 'Amount', e.target.value)}
                          />
                        </div>
                      </div>
                      {expenses.length > 1 && (
                        <div className="text-end mt-2">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm py-0 px-2"
                            onClick={() => removeExpenseRow(idx)}
                          >
                            <i className="bi bi-x-lg"></i> काढून टाका
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* DESKTOP RESPONSIVE TABLE VIEW (>= 768px) */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-bordered align-middle mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '40%' }}>खर्च प्रकार (Expense Product Item)</th>
                      <th style={{ width: '15%' }}>Qty</th>
                      <th style={{ width: '15%' }}>Rate</th>
                      <th style={{ width: '20%' }}>Amount (₹)</th>
                      <th style={{ width: '10%' }} className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((row, idx) => (
                      <tr key={idx}>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={row.product_id}
                            onChange={(e) => handleExpenseChange(idx, 'product_id', e.target.value)}
                          >
                            <option value="">-- खर्च निवडा --</option>
                            {products.map(p => (
                              <option key={p.Id} value={p.Id}>
                                {p.productName} ({p.unit})
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-center"
                            value={row.qty}
                            onChange={(e) => handleExpenseChange(idx, 'qty', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-center"
                            value={row.rate}
                            onChange={(e) => handleExpenseChange(idx, 'rate', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm text-center fw-bold text-danger"
                            value={row.Amount}
                            onChange={(e) => handleExpenseChange(idx, 'Amount', e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeExpenseRow(idx)}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="card-footer bg-light d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold small">एकूण बोट खर्च (Total Boat Expenses):</span>
                <span className="fs-5 fw-bold text-danger">₹{totalBoatExpenses.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* CREW SELECTION CARD (DEFAULT SELECTED & DESELECT IF NOT AVAILABLE) */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h6 className="fw-bold mb-0 text-dark">
                    <i className="bi bi-people me-2 text-info"></i>खलाशी निवड व भाग (Crew Selection - {selectedEmployees.length}/{allEmployees.length} Selected)
                  </h6>
                  <span className="small text-muted">सर्व खलाशी बाय-डिफॉल्ट निवडले आहेत. हजर नसलेले खलाशी अन-चेक (Deselect) करा.</span>
                </div>
                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-primary btn-sm rounded-pill px-3" onClick={selectAllCrew}>
                    <i className="bi bi-check-all me-1"></i> सर्व निवडा (Select All)
                  </button>
                  <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={deselectAllCrew}>
                    <i className="bi bi-dash-circle me-1"></i> सर्व रद्द (Deselect All)
                  </button>
                </div>
              </div>
              <div className="card-body p-3">
                <div className="row g-2 mb-3" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  {allEmployees.map(emp => {
                    const isSelected = selectedEmployees.some(e => e.emp_id === emp.id);
                    return (
                      <div key={emp.id} className="col-12 col-sm-6 col-md-4">
                        <div
                          className={`p-2 border rounded cursor-pointer d-flex justify-content-between align-items-center ${
                            isSelected ? 'border-primary bg-primary bg-opacity-10' : 'bg-light text-muted opacity-75'
                          }`}
                          onClick={() => toggleEmployee(emp)}
                          style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              onChange={() => {}}
                            />
                            <span className={`fw-semibold small ${isSelected ? 'text-primary' : 'text-dark'}`}>{emp.Emp_name}</span>
                          </div>
                          <span className={`badge ${isSelected ? 'bg-primary' : 'bg-secondary'}`}>
                            {emp.commission || 1} Pt
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* SELECTED CREW TABLE FOR SHARE ADJUSTMENT */}
                {selectedEmployees.length > 0 && (
                  <div className="border rounded p-3 bg-light">
                    <h6 className="fw-bold text-secondary mb-2 small text-uppercase">
                      <i className="bi bi-sliders me-1"></i>निवडलेले खलाशी वाटा पॉइंट (Share Points Adjustment)
                    </h6>
                    <div className="row g-2">
                      {selectedEmployees.map(emp => (
                        <div key={emp.emp_id} className="col-12 col-md-6 col-lg-4">
                          <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white small fw-semibold text-truncate" style={{ maxWidth: '140px' }}>
                              {emp.Emp_name}
                            </span>
                            <input
                              type="number"
                              step="0.5"
                              className="form-control text-center fw-bold"
                              value={emp.boat_commission}
                              onChange={(e) => handleCrewShareChange(emp.emp_id, e.target.value)}
                            />
                            <span className="input-group-text">Pts</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SAVE BUTTON */}
            <div className="d-flex gap-2 mb-4">
              <button type="submit" className="btn btn-success btn-lg w-100 fw-bold shadow-sm" disabled={loading}>
                <i className="bi bi-check-circle me-2"></i> पलट हिशोब सेव्ह करा (Save Boat Trip)
              </button>
              {tripId && (
                <button type="button" className="btn btn-outline-secondary btn-lg" onClick={resetForm}>
                  रद्द करा (Cancel Edit)
                </button>
              )}
            </div>
          </form>
        </div>

        {/* RIGHT PANEL: LIVE COMPUTATION SUMMARY & RECENT TRIPS LIST */}
        <div className="col-12 col-xl-4">
          {/* LIVE SETTLEMENT SUMMARY CARD */}
          <div className="card border-0 shadow-sm mb-4 bg-dark text-white">
            <div className="card-header bg-gradient bg-primary text-white fw-bold">
              <i className="bi bi-calculator me-2"></i>पलट जमा-खर्च हिशोब (Trip Settlement Summary)
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span>एकूण विक्री (Total Sales):</span>
                <span className="fw-bold text-success">₹{totalSales.toLocaleString('en-IN')}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>एकूण बोट खर्च (Expenses):</span>
                <span className="fw-bold text-danger">- ₹{totalBoatExpenses.toLocaleString('en-IN')}</span>
              </div>
              <hr className="border-secondary my-2" />
              <div className="d-flex justify-content-between mb-2 fs-6">
                <span>शिल्लक / तोटा (Net Margin):</span>
                <span className={`fw-bold ${(totalSales - totalBoatExpenses) >= 0 ? 'text-success' : 'text-danger'}`}>
                  ₹{(totalSales - totalBoatExpenses).toLocaleString('en-IN')}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2 fs-5">
                <span className="text-warning">५०% वाटा (50% Share):</span>
                <span className="fw-bold text-warning">₹{fiftyPercentShare.toLocaleString('en-IN')}</span>
              </div>
              <div className="d-flex justify-content-between mb-3 fs-6">
                <span className="text-warning">इतर वाटा / Point (Extra Vata):</span>
                <span className="fw-bold text-warning">+{parseFloat(addEmployee || 0)} Pt</span>
              </div>
              <hr className="border-secondary my-2" />
              <div className="d-flex justify-content-between mb-2 small text-muted">
                <span>निवडलेले खलाशी (Crew Base):</span>
                <span className="text-info fw-bold">{selectedEmployees.length} Members ({baseCrewShares} Pts)</span>
              </div>
              <div className="d-flex justify-content-between mb-2 small text-muted">
                <span>एकूण वाटा पॉइंट (Total Points):</span>
                <span className="text-info fw-bold">{totalCrewShares} Points</span>
              </div>
              <div className="p-3 bg-secondary bg-opacity-25 rounded mt-3 text-center border border-secondary">
                <span className="small text-uppercase text-light d-block mb-1">प्रति कामगार हिस्सा (Per Crew Share)</span>
                <h3 className="fw-bold text-info mb-0">₹{perEmployeePayout.toFixed(2)}</h3>
              </div>
            </div>
          </div>

          {/* RECENT TRIPS LIST */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3">
              <h6 className="fw-bold mb-0 text-dark">
                <i className="bi bi-clock-history me-2 text-primary"></i>मागील पलट हिशोब (Saved Trips)
              </h6>
            </div>
            <div className="table-responsive" style={{ maxHeight: '400px' }}>
              <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>पलट #</th>
                    <th>तारीख</th>
                    <th>५०% वाटा</th>
                    <th>इतर वाटा (Point)</th>
                    <th className="text-end">कृती</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map(t => (
                    <tr key={t.id}>
                      <td className="fw-bold">#{t.boat_no}</td>
                      <td>{t.boat_date}</td>
                      <td className="text-success fw-bold">₹{(t.fifty_percent || 0).toLocaleString('en-IN')}</td>
                      <td>
                        <span className="badge bg-warning text-dark px-2 py-1">
                          +{parseFloat(t.add_employee || 0)} Pt
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-outline-primary btn-sm me-1 py-0 px-2"
                          onClick={() => handleEditTrip(t)}
                          title="Edit Trip"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm py-0 px-2"
                          onClick={() => handleDeleteTrip(t.id)}
                          title="Delete Trip"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoatExpensesEntry;
